import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../lib/auth";

export default function Login() {
  const { login, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const redirectTo = location.state?.from || "/brand-os";

  async function handleSubmit(e) {
    e.preventDefault();
    setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (res.ok) navigate(redirectTo, { replace: true });
  }

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
  // THIS BREAKS THE AUTH.
  function handleGoogleSignIn() {
    const redirectUrl = window.location.origin + "/brand-os/auth/callback";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" +
      encodeURIComponent(redirectUrl);
  }

  return (
    <div className="bo-root" data-testid="login-screen">
      <div className="bo-login-shell">
        <div className="bo-login-card">
          <div className="bo-login-eyebrow" data-testid="login-eyebrow">
            Precipice Brand OS · Gate 1
          </div>
          <h1 className="bo-login-title" data-testid="login-title">
            Sign in to your workspace.
          </h1>
          <p className="bo-login-lede">
            The marketing brain for Precipice Health. Editorial. Governed. Yours.
          </p>

          <form className="bo-login-form" onSubmit={handleSubmit} data-testid="login-form">
            <div className="bo-field">
              <label className="bo-label" htmlFor="bo-login-email">Email</label>
              <input
                id="bo-login-email"
                className="bo-input"
                data-testid="login-email-input"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@precipice-health.com"
                disabled={busy}
              />
            </div>
            <div className="bo-field">
              <label className="bo-label" htmlFor="bo-login-password">Password</label>
              <input
                id="bo-login-password"
                className="bo-input"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
              <Link
                to="/brand-os/forgot-password"
                data-testid="login-forgot-password-link"
                style={{
                  alignSelf: "flex-end",
                  marginTop: 4,
                  fontSize: 12,
                  color: "var(--bo-muted, #6b6f76)",
                  textDecoration: "none",
                }}
              >
                Forgot password?
              </Link>
              <Link
                to="/brand-os/signup"
                data-testid="login-signup-link"
                style={{
                  alignSelf: "flex-end",
                  marginTop: 2,
                  fontSize: 12,
                  color: "var(--bo-muted, #6b6f76)",
                  textDecoration: "none",
                }}
              >
                New here? Create a workspace →
              </Link>
            </div>

            {error && (
              <div className="bo-login-error" data-testid="login-error" role="alert">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="bo-btn bo-btn-primary"
              data-testid="login-submit-button"
              disabled={busy || !email || !password}
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              margin: "18px 0 12px",
              color: "var(--bo-muted, #6b6f76)",
              fontSize: 12,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
            aria-hidden
          >
            <div
              style={{
                flex: 1,
                height: 1,
                background: "var(--bo-border, #e6e2d8)",
              }}
            />
            or
            <div
              style={{
                flex: 1,
                height: 1,
                background: "var(--bo-border, #e6e2d8)",
              }}
            />
          </div>

          <button
            type="button"
            className="bo-btn bo-btn--secondary"
            onClick={handleGoogleSignIn}
            data-testid="login-google-button"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              padding: "10px 14px",
            }}
            disabled={busy}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              aria-hidden
              focusable="false"
            >
              <path
                fill="#4285F4"
                d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.79 2.72v2.26h2.9c1.7-1.56 2.69-3.87 2.69-6.62z"
              />
              <path
                fill="#34A853"
                d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.9-2.26c-.81.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.34A9 9 0 0 0 9 18z"
              />
              <path
                fill="#FBBC05"
                d="M3.95 10.7A5.4 5.4 0 0 1 3.66 9c0-.59.1-1.16.29-1.7V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l2.99-2.34z"
              />
              <path
                fill="#EA4335"
                d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.88 11.43 0 9 0A9 9 0 0 0 .96 4.96l2.99 2.34C4.66 5.17 6.65 3.58 9 3.58z"
              />
            </svg>
            Sign in with Google
          </button>

          <div className="bo-login-footer">
            Secure session cookie · role-scoped access · all activity audited.
          </div>
        </div>
      </div>
    </div>
  );
}
